import React, { useState, useEffect, useRef } from 'react'
import { Mail, FileText, Bell, Send, RefreshCw, Search, Trash2, RotateCcw, Plus, X, Sparkles, Image, Upload } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'

const API_BASE = '/api'

// AI Writing Assistant Component
function AIWritingAssistant({ currentText, onApply, type = 'email' }) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestion, setSuggestion] = useState('')
  const [mode, setMode] = useState('write')

  const generateWithAI = async () => {
    if (!prompt.trim() && mode === 'write') { alert('Please describe what you want to write'); return }
    setLoading(true); setSuggestion('')
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/ai/write`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, current_text: currentText, mode, type })
      })
      if (response.ok) { const data = await response.json(); setSuggestion(data.text || '') }
      else { const data = await response.json(); alert(data.detail || 'Failed to generate text') }
    } catch (error) { alert('Failed to generate text') }
    finally { setLoading(false) }
  }

  const quickPrompts = type === 'email' ? [
    { label: 'Welcome new user', prompt: 'Write a warm welcome email for a new user who just signed up' },
    { label: 'Trial ending', prompt: 'Write a friendly reminder that their trial is ending soon' },
    { label: 'Feature announcement', prompt: 'Announce a new feature update' },
    { label: 'Re-engagement', prompt: 'Write an email to re-engage inactive users' },
  ] : [
    { label: 'Product update', prompt: 'Announce a new product feature or improvement' },
    { label: 'Maintenance notice', prompt: 'Notify users about scheduled maintenance' },
    { label: 'Tips & tricks', prompt: 'Share helpful tips for using the platform' },
  ]

  return (
    <Card className="border-purple-200 bg-purple-50/50">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-purple-700"><Sparkles className="w-5 h-5" /><span className="font-medium">AI Writing Assistant</span></div>
        <div className="flex gap-2">
          {['write', 'rewrite', 'improve'].map(m => (
            <Button key={m} size="sm" variant={mode === m ? 'default' : 'outline'} onClick={() => setMode(m)} disabled={m !== 'write' && !currentText} className={mode === m ? 'bg-purple-600 hover:bg-purple-700' : ''}>{m === 'write' ? 'Write New' : m.charAt(0).toUpperCase() + m.slice(1)}</Button>
          ))}
        </div>
        {mode === 'write' && (
          <div className="flex flex-wrap gap-2">
            {quickPrompts.map((qp, i) => <button key={i} onClick={() => setPrompt(qp.prompt)} className="px-2 py-1 text-xs bg-white border border-purple-200 rounded-lg hover:bg-purple-50">{qp.label}</button>)}
          </div>
        )}
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder={mode === 'write' ? 'Describe what you want to write...' : mode === 'rewrite' ? 'How should it be rewritten?' : 'What improvements would you like?'}
          rows={2} className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white" />
        <Button onClick={generateWithAI} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
          {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate with AI</>}
        </Button>
        {suggestion && (
          <div className="space-y-2">
            <div className="p-3 bg-white border border-purple-200 rounded-lg text-sm text-gray-800 whitespace-pre-wrap">{suggestion}</div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => onApply(suggestion)}>Use This</Button>
              <Button variant="outline" onClick={() => setSuggestion('')}>Discard</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Posts Tab
function PostsTab() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [showNewPost, setShowNewPost] = useState(false)
  const [newPost, setNewPost] = useState({ title: '', content: '', type: 'announcement', media: [] })
  const [creating, setCreating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => { fetchPosts() }, [])

  const fetchPosts = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/posts`, { headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) { const data = await response.json(); setPosts(data.posts || []) }
    } catch (error) { console.error('Error fetching posts:', error) }
    finally { setLoading(false) }
  }

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/feed/posts/${postId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) setPosts(posts.filter(p => p.post_id !== postId))
    } catch (error) { console.error('Error deleting post:', error) }
  }

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return
    setUploading(true)
    const newMedia = []
    for (const file of files) {
      try {
        const reader = new FileReader()
        const base64 = await new Promise((resolve, reject) => { reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file) })
        newMedia.push(base64)
      } catch (error) { console.error('Error uploading file:', error) }
    }
    setNewPost(prev => ({ ...prev, media: [...prev.media, ...newMedia] }))
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) { alert('Please enter post content'); return }
    setCreating(true)
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/posts/global`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ content: newPost.content, post_type: newPost.type, media: newPost.media, pinned: false }) })
      if (response.ok) { setShowNewPost(false); setNewPost({ title: '', content: '', type: 'announcement', media: [] }); fetchPosts(); alert('Post created successfully') }
      else { const data = await response.json(); alert(data.detail || 'Failed to create post') }
    } catch (error) { alert('Failed to create post') }
    finally { setCreating(false) }
  }

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.content?.toLowerCase().includes(search.toLowerCase()) || post.author_name?.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || (filter === 'flagged' && post.flagged) || (filter === 'hidden' && post.hidden)
    return matchesSearch && matchesFilter
  })

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search posts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Posts</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="ml-3" onClick={() => setShowNewPost(true)}><Plus className="w-4 h-4 mr-2" />New Post</Button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {filteredPosts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No posts found</div>
          ) : (
            filteredPosts.map(post => (
              <div key={post.post_id} className="p-4 hover:bg-gray-50">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    {post.media?.length > 0 ? <img src={post.media[0]} alt="" className="w-16 h-16 rounded-lg object-cover bg-gray-100" /> : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center"><FileText className="w-6 h-6 text-gray-400" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{post.author_name || 'Unknown'}</span>
                      <span className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString()}</span>
                      {post.media?.length > 0 && <Badge className="bg-blue-100 text-blue-700"><Image className="w-3 h-3 mr-1" />{post.media.length}</Badge>}
                      {post.flagged && <Badge className="bg-rose-100 text-rose-700">Flagged</Badge>}
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2">{post.content}</p>
                    <p className="text-xs text-gray-400 mt-1">Team: {post.team_name || 'Global'}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="text-gray-400 hover:text-rose-500" onClick={() => handleDeletePost(post.post_id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Global Post</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title (optional)</Label><Input value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} placeholder="Post title..." /></div>
            <div><Label>Content</Label><textarea value={newPost.content} onChange={(e) => setNewPost({ ...newPost, content: e.target.value })} placeholder="Write your announcement..." rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" /></div>
            <AIWritingAssistant currentText={newPost.content} onApply={(text) => setNewPost({ ...newPost, content: text })} type="post" />
            <div>
              <Label>Photos</Label>
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" multiple className="hidden" />
              <div className="flex flex-wrap gap-3 mt-2">
                {newPost.media.map((media, index) => (
                  <div key={index} className="relative group">
                    <img src={media} alt="" className="w-20 h-20 rounded-lg object-cover" />
                    <button onClick={() => setNewPost(prev => ({ ...prev, media: prev.media.filter((_, i) => i !== index) }))} className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-gray-400">
                  {uploading ? <RefreshCw className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6" /><span className="text-xs mt-1">Add</span></>}
                </button>
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newPost.type} onValueChange={(val) => setNewPost({ ...newPost, type: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="update">Product Update</SelectItem>
                  <SelectItem value="maintenance">Maintenance Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNewPost(false)}>Cancel</Button>
              <Button onClick={handleCreatePost} disabled={creating}>{creating ? 'Creating...' : 'Create Post'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Alerts Tab
function AlertsTab() {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNewAlert, setShowNewAlert] = useState(false)
  const [newAlert, setNewAlert] = useState({ title: '', message: '', severity: 'info', target: 'all' })
  const [creating, setCreating] = useState(false)

  useEffect(() => { fetchAlerts() }, [])

  const fetchAlerts = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/alerts`, { headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) { const data = await response.json(); setAlerts(data.alerts || []) }
    } catch (error) { console.error('Error fetching alerts:', error) }
    finally { setLoading(false) }
  }

  const handleDismissAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('admin_token')
      await fetch(`${API_BASE}/admin/alerts/${alertId}/dismiss`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      setAlerts(alerts.filter(a => a.alert_id !== alertId))
    } catch (error) { console.error('Error dismissing alert:', error) }
  }

  const handleCreateAlert = async () => {
    if (!newAlert.title.trim() || !newAlert.message.trim()) { alert('Please fill in all required fields'); return }
    setCreating(true)
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/alerts`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(newAlert) })
      if (response.ok) { setShowNewAlert(false); setNewAlert({ title: '', message: '', severity: 'info', target: 'all' }); fetchAlerts(); alert('Alert created successfully') }
      else { const data = await response.json(); alert(data.detail || 'Failed to create alert') }
    } catch (error) { alert('Failed to create alert') }
    finally { setCreating(false) }
  }

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-500">System Alerts</h3>
        <Button onClick={() => setShowNewAlert(true)}><Plus className="w-4 h-4 mr-2" />New Alert</Button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No alerts</div>
          ) : (
            alerts.map(a => (
              <div key={a.alert_id} className={`p-4 ${a.severity === 'high' ? 'bg-rose-50' : a.severity === 'medium' ? 'bg-amber-50' : ''}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={a.severity === 'high' ? 'bg-rose-100 text-rose-700' : a.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>{a.severity || 'info'}</Badge>
                      <span className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    <p className="font-medium text-gray-900">{a.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{a.message}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDismissAlert(a.alert_id)}>Dismiss</Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={showNewAlert} onOpenChange={setShowNewAlert}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create System Alert</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={newAlert.title} onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })} placeholder="Alert title..." /></div>
            <div><Label>Message *</Label><textarea value={newAlert.message} onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })} placeholder="Alert message..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Severity</Label>
                <Select value={newAlert.severity} onValueChange={(val) => setNewAlert({ ...newAlert, severity: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="info">Info</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target</Label>
                <Select value={newAlert.target} onValueChange={(val) => setNewAlert({ ...newAlert, target: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem><SelectItem value="admins">Admins Only</SelectItem><SelectItem value="team_owners">Team Owners</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNewAlert(false)}>Cancel</Button>
              <Button onClick={handleCreateAlert} disabled={creating}>{creating ? 'Creating...' : 'Create Alert'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Emails Tab
function EmailsTab() {
  const [emails, setEmails] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [showSendEmail, setShowSendEmail] = useState(false)
  const [emailForm, setEmailForm] = useState({ recipient_type: 'individual', to_email: '', subject: '', message: '', user_segment: 'all' })
  const [sending, setSending] = useState(false)

  useEffect(() => { fetchEmails() }, [])

  const fetchEmails = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/emails`, { headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) { const data = await response.json(); setEmails(data.emails || []) }
    } catch (error) { console.error('Error fetching emails:', error); setEmails([]) }
    finally { setLoading(false) }
  }

  const handleResendEmail = async (emailId) => {
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/emails/${emailId}/resend`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      if (response.ok) { alert('Email resent successfully'); fetchEmails() }
    } catch (error) { alert('Failed to resend email') }
  }

  const handleSendEmail = async () => {
    if (emailForm.recipient_type === 'individual' && !emailForm.to_email.trim()) { alert('Please enter recipient email'); return }
    if (!emailForm.subject.trim() || !emailForm.message.trim()) { alert('Please fill in subject and message'); return }
    setSending(true)
    try {
      const token = localStorage.getItem('admin_token')
      const response = await fetch(`${API_BASE}/admin/emails/send`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(emailForm) })
      if (response.ok) {
        const data = await response.json()
        setShowSendEmail(false); setEmailForm({ recipient_type: 'individual', to_email: '', subject: '', message: '', user_segment: 'all' }); fetchEmails()
        alert(data.message || 'Email sent successfully')
      } else { const data = await response.json(); alert(data.detail || 'Failed to send email') }
    } catch (error) { alert('Failed to send email') }
    finally { setSending(false) }
  }

  const filteredEmails = emails.filter(email => {
    const matchesSearch = email.to_email?.toLowerCase().includes(search.toLowerCase()) || email.subject?.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || (filter === 'sent' && email.status === 'sent') || (filter === 'failed' && email.status === 'failed') || (filter === 'invite' && email.type === 'invite') || (filter === 'trial' && email.type?.includes('trial'))
    return matchesSearch && matchesFilter
  })

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Emails', value: emails.length, cls: 'text-gray-900' },
          { label: 'Delivered', value: emails.filter(e => e.status === 'sent').length, cls: 'text-emerald-600' },
          { label: 'Failed', value: emails.filter(e => e.status === 'failed').length, cls: 'text-rose-500' },
          { label: 'Invites Sent', value: emails.filter(e => e.type === 'invite').length, cls: 'text-blue-600' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-4"><p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p><p className="text-sm text-gray-500">{s.label}</p></CardContent></Card>
        ))}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search by email or subject..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Emails</SelectItem><SelectItem value="sent">Delivered</SelectItem><SelectItem value="failed">Failed</SelectItem><SelectItem value="invite">Invites</SelectItem><SelectItem value="trial">Trial Emails</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchEmails}><RefreshCw className="w-4 h-4" /></Button>
        <Button onClick={() => setShowSendEmail(true)}><Send className="w-4 h-4 mr-2" />Send Email</Button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {filteredEmails.length === 0 ? (
            <div className="p-8 text-center text-gray-500"><Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>No emails found</p></div>
          ) : (
            filteredEmails.map(email => (
              <div key={email.email_id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${email.status === 'sent' ? 'bg-emerald-500' : email.status === 'failed' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                      <span className="font-medium text-gray-900 truncate">{email.to_email}</span>
                      <Badge className={email.type === 'invite' ? 'bg-blue-100 text-blue-700' : email.type?.includes('trial') ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}>{email.type || 'general'}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{email.subject}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(email.sent_at).toLocaleString()}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleResendEmail(email.email_id)} title="Resend"><RotateCcw className="w-4 h-4" /></Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={showSendEmail} onOpenChange={setShowSendEmail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Send Email</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient Type</Label>
              <div className="flex gap-4 mt-2">
                {['individual', 'segment'].map(t => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="recipient_type" value={t} checked={emailForm.recipient_type === t} onChange={(e) => setEmailForm({ ...emailForm, recipient_type: e.target.value })} className="text-indigo-600" />
                    <span className="text-sm text-gray-800 capitalize">{t}</span>
                  </label>
                ))}
              </div>
            </div>
            {emailForm.recipient_type === 'individual' ? (
              <div><Label>Recipient Email *</Label><Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="user@example.com" /></div>
            ) : (
              <div>
                <Label>User Segment</Label>
                <Select value={emailForm.user_segment} onValueChange={(val) => setEmailForm({ ...emailForm, user_segment: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem><SelectItem value="team_owners">Team Owners</SelectItem><SelectItem value="trial_users">Trial Users</SelectItem><SelectItem value="premium_users">Premium Users</SelectItem><SelectItem value="inactive">Inactive Users (30+ days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Subject *</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Email subject..." /></div>
            <div>
              <Label>Message *</Label>
              <textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Write your email message..." rows={5} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
              <p className="text-xs text-gray-400 mt-1">Use {'{name}'} for personalization.</p>
            </div>
            <AIWritingAssistant currentText={emailForm.message} onApply={(text) => setEmailForm({ ...emailForm, message: text })} type="email" />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowSendEmail(false)}>Cancel</Button>
              <Button onClick={handleSendEmail} disabled={sending}>{sending ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Email</>}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Main
export default function Communications() {
  return (
    <div className="space-y-6">
      <PageHeader title="Communications" description="Manage emails, posts, and system alerts" />

      <Tabs defaultValue="emails">
        <TabsList>
          <TabsTrigger value="emails"><Mail className="w-4 h-4 mr-1" />Emails</TabsTrigger>
          <TabsTrigger value="posts"><FileText className="w-4 h-4 mr-1" />Posts</TabsTrigger>
          <TabsTrigger value="alerts"><Bell className="w-4 h-4 mr-1" />Alerts</TabsTrigger>
        </TabsList>
        <TabsContent value="emails"><EmailsTab /></TabsContent>
        <TabsContent value="posts"><PostsTab /></TabsContent>
        <TabsContent value="alerts"><AlertsTab /></TabsContent>
      </Tabs>
    </div>
  )
}
